import tokens from '@kluein/design-tokens';
import { Banner } from '@kluein/klue-ui';
import {
  Button,
  Collapse,
  Group,
  Loader,
  Modal,
  Radio,
  Stack,
  Switch,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  Title,
} from '@mantine/core';
import {
  CheckCircleIcon,
  DownloadSimpleIcon,
  ExportIcon,
  FileDocIcon,
  InfoIcon,
  PresentationChartIcon,
  ShieldWarningIcon,
  X,
} from '@phosphor-icons/react';
import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  exportContent,
  triggerDownload,
} from 'modules/current-compete/libs/ask-klue-research-mode-chat/api/export.api';

import type {
  ConfidentialityOptions,
  ExportFormat,
  ExportResult,
} from 'modules/current-compete/libs/ask-klue-research-mode-chat/api/export.api.types';
import type { ReactNode } from 'react';

type ExportModalProps = {
  opened: boolean;
  onClose: () => void;
  messageId: string;
};

type ModalState = 'form' | 'loading' | 'complete' | 'error';

function ExportModal({
  opened,
  onClose,
  messageId,
}: ExportModalProps): ReactNode {
  const { t } = useTranslation(['Export', 'Common']);
  const [modalState, setModalState] = useState<ModalState>('form');
  const [exportFormat, setExportFormat] = useState<ExportFormat>('docx');
  const [customInstructions, setCustomInstructions] = useState('');
  const [confidentialityEnabled, setConfidentialityEnabled] = useState(false);
  const [headerText, setHeaderText] = useState('');
  const [watermarkText, setWatermarkText] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const resultRef = useRef<ExportResult | null>(null);

  const resetToForm = useCallback(() => {
    setModalState('form');
    setErrorMessage('');
    resultRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    setModalState('form');
    setExportFormat('docx');
    setCustomInstructions('');
    setConfidentialityEnabled(false);
    setHeaderText('');
    setWatermarkText('');
    setErrorMessage('');
    resultRef.current = null;
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    setModalState('loading');
    setErrorMessage('');

    const confidentiality: ConfidentialityOptions | undefined =
      exportFormat === 'pptx' && confidentialityEnabled
        ? {
            enabled: true,
            headerText: headerText.trim() || undefined,
            watermarkText: watermarkText.trim() || undefined,
          }
        : undefined;

    try {
      const result = await exportContent({
        messageId,
        exportFormat,
        mode: 'exact',
        customInstructions: customInstructions.trim() || undefined,
        confidentiality,
      });

      resultRef.current = result;
      triggerDownload(result.blob, result.filename);
      setModalState('complete');
    } catch {
      setErrorMessage(t('Export:modal.error.message'));
      setModalState('error');
    }
  }, [
    messageId,
    exportFormat,
    customInstructions,
    confidentialityEnabled,
    headerText,
    watermarkText,
    t,
  ]);

  const handleManualDownload = useCallback(() => {
    if (resultRef.current) {
      triggerDownload(resultRef.current.blob, resultRef.current.filename);
    }
  }, []);

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size={600}
      centered
      title={
        <Group gap="sm" align="center">
          <ExportIcon />
          <Title order={2} size="h4">
            {t('Export:modal.title')}
          </Title>
        </Group>
      }
      closeButtonProps={{
        icon: <X />,
      }}
      closeOnClickOutside={modalState !== 'loading'}
      closeOnEscape={modalState !== 'loading'}
      withCloseButton={modalState !== 'loading'}
      styles={{
        header: {
          backgroundColor: 'var(--tokens-color-primary-main)',
          color: 'white',
        },
        title: {
          color: 'white',
        },
        close: {
          color: 'white',
          backgroundColor: 'transparent',
          background: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent !important',
            background: 'transparent !important',
          },
        },
        body: {
          padding: 0,
        },
      }}
    >
      <Stack gap="md" py={tokens.spacing.medium} px={tokens.spacing.xlarge}>
        {modalState === 'loading' && (
          <Stack align="center" gap="md" py="xl">
            <Loader size="lg" />
            <Text size="sm" c="dimmed">
              {exportFormat === 'docx'
                ? t('Export:modal.loading.generatingDocument')
                : t('Export:modal.loading.generatingPresentation')}
            </Text>
            <Text size="xs" c="dimmed">
              {t('Export:modal.loading.estimate')}
            </Text>
          </Stack>
        )}

        {modalState === 'complete' && (
          <Stack align="center" gap="md" py="xl">
            <CheckCircleIcon
              size={48}
              weight="duotone"
              color="var(--mantine-color-green-6)"
            />
            <Text size="lg" fw={500}>
              {t('Export:modal.complete.title')}
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              {t('Export:modal.complete.description')}
            </Text>
            <Group justify="center" gap="sm">
              <Button
                variant="filled"
                leftSection={<DownloadSimpleIcon size={20} />}
                onClick={handleManualDownload}
              >
                {t('Export:modal.complete.download')}
              </Button>
              <Button variant="subtle" color="gray" onClick={resetToForm}>
                {t('Export:modal.complete.exportAgain')}
              </Button>
            </Group>
          </Stack>
        )}

        {modalState === 'error' && (
          <Stack align="center" gap="md" py="xl">
            <Banner
              variant="inline"
              status="error"
              message={errorMessage}
              show
            />
            <Button variant="filled" onClick={resetToForm}>
              {t('Export:modal.error.tryAgain')}
            </Button>
          </Stack>
        )}

        {modalState === 'form' && (
          <>
            <Text fw={700} fz={tokens.fontSize.headingFont.small}>
              {t('Export:modal.format.label')}
            </Text>
            <Radio.Group
              value={exportFormat}
              onChange={(value) => setExportFormat(value as ExportFormat)}
            >
              <Group gap="sm">
                <Radio
                  value="docx"
                  label={
                    <Group gap="xs">
                      <FileDocIcon size={18} weight="duotone" />
                      <Text size="sm">{t('Export:modal.format.docx')}</Text>
                    </Group>
                  }
                />
                <Radio
                  value="pptx"
                  label={
                    <Group gap="xs">
                      <PresentationChartIcon size={18} weight="duotone" />
                      <Text size="sm">{t('Export:modal.format.pptx')}</Text>
                    </Group>
                  }
                />
              </Group>
            </Radio.Group>

            <Textarea
              label={
                <Group gap={4}>
                  <Text size="sm" fw={500}>
                    {t('Export:modal.customInstructions.label')}
                  </Text>
                  <Tooltip
                    label={t('Export:modal.customInstructions.tooltip')}
                    position="top"
                    withArrow
                    multiline
                    w={420}
                  >
                    <span style={{ display: 'flex', cursor: 'pointer' }}>
                      <InfoIcon size={14} color="var(--mantine-color-gray-6)" />
                    </span>
                  </Tooltip>
                </Group>
              }
              placeholder={t('Export:modal.customInstructions.placeholder')}
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.currentTarget.value)}
              minRows={2}
              maxRows={4}
            />

            {exportFormat === 'pptx' && (
              <Stack
                gap="sm"
                p="md"
                style={{
                  border: '1px solid var(--mantine-color-gray-3)',
                  borderRadius: 'var(--mantine-radius-md)',
                  backgroundColor: confidentialityEnabled
                    ? 'var(--mantine-color-red-0)'
                    : undefined,
                }}
              >
                <Switch
                  checked={confidentialityEnabled}
                  onChange={(e) =>
                    setConfidentialityEnabled(e.currentTarget.checked)
                  }
                  label={
                    <Group gap="xs">
                      <ShieldWarningIcon size={18} weight="duotone" />
                      <Text size="sm" fw={500}>
                        {t('Export:modal.confidentiality.label')}
                      </Text>
                    </Group>
                  }
                />

                <Collapse in={confidentialityEnabled}>
                  <Stack gap="sm" mt="sm">
                    <TextInput
                      label={t('Export:modal.confidentiality.headerText.label')}
                      placeholder={t(
                        'Export:modal.confidentiality.headerText.placeholder',
                      )}
                      value={headerText}
                      onChange={(e) => setHeaderText(e.currentTarget.value)}
                      size="sm"
                    />
                    <TextInput
                      label={t(
                        'Export:modal.confidentiality.watermarkText.label',
                      )}
                      placeholder={t(
                        'Export:modal.confidentiality.watermarkText.placeholder',
                      )}
                      value={watermarkText}
                      onChange={(e) => setWatermarkText(e.currentTarget.value)}
                      size="sm"
                    />
                  </Stack>
                </Collapse>
              </Stack>
            )}

            <Group justify="flex-end" gap="sm" mt="md">
              <Button variant="subtle" color="gray" onClick={handleClose}>
                {t('Common:actions.cancel')}
              </Button>
              <Button variant="filled" onClick={handleSubmit}>
                {t('Export:modal.export')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}

export default ExportModal;
